import type { OutputResolution, Size } from '../geometry/framing.ts';
import { effectiveFrameRate, estimateVideoBitrate } from './estimate.ts';
import type { QualityId } from './recipe.ts';

/**
 * "Keep it under N MB": the video bitrate that makes a file fit a size, from
 * the plain sum the file is made of —
 *
 *   video bitrate = (limit × 8 × 0.96 − sound bitrate × length) / length
 *
 * The 4% is room for the container (MP4 boxes, the index) and an encoder that
 * runs a little over. Below a picture-quality floor the picture gets smaller,
 * one step at a time, and the person is told; when even the smallest step
 * can't fit, they're told how long a clip does.
 */

/** One megabyte as phones, mail and chat apps count it (decimal, the smaller of the two readings: always safe). */
export const BYTES_PER_MB = 1_000_000;
/** Share of the limit the sound and picture may use; the rest is container and encoder slack. */
export const SIZE_MARGIN = 0.96;
/**
 * Bits per pixel per frame below which H.264 turns to mush on camera footage
 * (Shycut's own "small" quality is 0.05). Below it, a smaller picture looks better.
 */
export const MIN_BITS_PER_PIXEL = 0.04;
/** Short sides the picture steps down through, largest first; never below 360. */
export const SIZE_STEPS: readonly number[] = [1080, 720, 540, 480, 360];
/** Sound bitrate within a limit: plenty for speech and music… */
export const SOUND_BITRATE = 128_000;
/** …and when sound would take more than a fifth of a tight limit, a little less. */
export const TIGHT_SOUND_BITRATE = 96_000;
const TIGHT_SOUND_SHARE = 0.2;
/** Limits the field accepts, in MB. */
export const SIZE_LIMIT_RANGE = { min: 1, max: 4000 } as const;

/** Units services state their limits in. */
export type SizeUnit = 'MB' | 'MiB' | 'GB';

/** A size as a service states it ("20 MiB", "4 GB"). */
export interface StatedSize {
  readonly value: number;
  readonly unit: SizeUnit;
}

/** A stated size in decimal MB, the unit the app aims in. */
export const sizeInMb = ({ value, unit }: StatedSize): number => (unit === 'MB' ? value : unit === 'MiB' ? (value * 1024 * 1024) / BYTES_PER_MB : value * 1000);

export interface SizePlanInput {
  readonly limitMb: number;
  readonly durationSec: number;
  readonly hasAudio: boolean;
  /** The picture's size before any step down. */
  readonly size: Size;
  readonly frameRate: number;
  /** The quality chosen; a roomy limit never makes the file bigger than this quality would. */
  readonly quality: QualityId;
}

export type SizePlan =
  | {
      readonly kind: 'fits';
      readonly videoBitrate: number;
      readonly audioBitrate: number;
      readonly size: Size;
      /** Set when the picture was made smaller to fit: the short side it was stepped down to. */
      readonly steppedTo: number | null;
      /** What the file should come to, in bytes (at most the limit). */
      readonly expectedBytes: number;
    }
  | {
      readonly kind: 'too-long';
      /** The longest clip that fits the limit at the smallest picture. */
      readonly maxDurationSec: number;
    };

const even = (value: number): number => Math.max(2, Math.round(value / 2) * 2);

/** `size` with its short side at `shortSide` (never larger than it is), keeping its shape. */
export function withShortSide(size: Size, shortSide: number): Size {
  const scale = Math.min(1, shortSide / Math.min(size.width, size.height));
  return { width: even(size.width * scale), height: even(size.height * scale) };
}

/** The lowest bitrate a picture of this size and rate still looks acceptable at. */
export const floorBitrate = (size: Size, frameRate: number): number => Math.round(size.width * size.height * effectiveFrameRate(frameRate) * MIN_BITS_PER_PIXEL);

/** The sizes to try: as it is, then every smaller step. */
function candidates(size: Size): { readonly size: Size; readonly steppedTo: number | null }[] {
  const short = Math.min(size.width, size.height);
  return [{ size, steppedTo: null }, ...SIZE_STEPS.filter((step) => step < short).map((step) => ({ size: withShortSide(size, step), steppedTo: step }))];
}

export function planSize(input: SizePlanInput): SizePlan {
  const budgetBits = input.limitMb * BYTES_PER_MB * 8 * SIZE_MARGIN;
  const duration = Math.max(input.durationSec, 0.1);
  const sound = !input.hasAudio ? 0 : SOUND_BITRATE * duration > budgetBits * TIGHT_SOUND_SHARE ? TIGHT_SOUND_BITRATE : SOUND_BITRATE;
  const videoBudget = (budgetBits - sound * duration) / duration;
  for (const candidate of candidates(input.size)) {
    if (videoBudget < floorBitrate(candidate.size, input.frameRate)) continue;
    // Rounded down: a bit over is a file over.
    const videoBitrate = Math.floor(Math.min(videoBudget, estimateVideoBitrate(candidate.size, input.frameRate, input.quality)));
    return {
      kind: 'fits',
      videoBitrate,
      audioBitrate: sound,
      size: candidate.size,
      steppedTo: candidate.steppedTo,
      expectedBytes: Math.floor(((videoBitrate + sound) * duration) / 8 / SIZE_MARGIN),
    };
  }
  const smallest = candidates(input.size).at(-1)!.size;
  return { kind: 'too-long', maxDurationSec: longestClip(input.limitMb, floorBitrate(smallest, input.frameRate), input.hasAudio) };
}

/**
 * The longest clip, in whole seconds, whose picture still gets `videoBitrate`
 * within a limit: the budget sum above solved for the length, with the sound
 * `planSize` would pick for that length.
 */
export function longestClip(limitMb: number, videoBitrate: number, hasAudio: boolean): number {
  const budgetBits = limitMb * BYTES_PER_MB * 8 * SIZE_MARGIN;
  if (!hasAudio) return Math.floor(budgetBits / videoBitrate);
  const withFullSound = budgetBits / (videoBitrate + SOUND_BITRATE);
  // At that length full sound would take over a fifth of the limit, so the lighter sound applies and a little more fits.
  const tight = SOUND_BITRATE * withFullSound > budgetBits * TIGHT_SOUND_SHARE;
  return Math.floor(tight ? budgetBits / (videoBitrate + TIGHT_SOUND_BITRATE) : withFullSound);
}

/** The resolution to make the output at: the chosen one, or the step a limit needed. */
export const resolutionFor = (steppedTo: number | null, current: OutputResolution): OutputResolution => (steppedTo === null ? current : (String(steppedTo) as OutputResolution));

/**
 * A second try after a file came out over its limit (an encoder that ran
 * hot): the bitrate scaled by how far it overshot, with 3% more room.
 */
export const correctedBitrate = (videoBitrate: number, actualBytes: number, limitBytes: number): number =>
  Math.max(1, Math.floor(videoBitrate * (limitBytes / Math.max(1, actualBytes)) * 0.97));

/** A typed limit, or null when it isn't a usable number of MB. */
export function parseSizeLimit(value: unknown): number | null {
  const mb = typeof value === 'number' ? value : typeof value === 'string' ? Number(value.trim().replace(',', '.')) : Number.NaN;
  if (!Number.isFinite(mb) || mb < SIZE_LIMIT_RANGE.min || mb > SIZE_LIMIT_RANGE.max) return null;
  return Math.round(mb * 10) / 10;
}
